import {
  ChangeEvent,
  CSSProperties,
  FC,
  ReactNode,
  useRef,
  useState,
} from 'react';
import { MeResponse } from 'components/login/types';
import { MemberResponse } from 'lib/teams';
import Avatar from '../common/Avatar';
import styles from './Field.module.css';
import { join, insertIf } from 'lib/helpers';

type FieldProps = {
  children?: ReactNode;
  name?: string;
  label: string;
  placeholder?: string;
  value?: string;
  alt?: string;
  type?: string;
  required?: boolean;
  avatar?: string | 'initial' | null;
  data?: MeResponse | MemberResponse | null;
  onFocus?: (event: ChangeEvent<HTMLInputElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocusOut?: (event: ChangeEvent<HTMLInputElement>) => void;
  paddingOverride?: string;
  boldLabel?: boolean;
  editable?: boolean;
  fullWidth?: boolean;
  minLength?: number;
  maxLength?: number;
  nonEditableValueStyle?: CSSProperties;
  show?: boolean;
  autoFocus?: boolean;
  suppressPasswordManagers?: boolean;
};

const Field: FC<FieldProps> = ({
  name,
  label,
  placeholder,
  value,
  alt,
  type,
  data,
  onFocus,
  onChange,
  onFocusOut,
  required = false,
  editable = false,
  fullWidth = false,
  boldLabel = false,
  paddingOverride,
  minLength,
  maxLength,
  children,
  nonEditableValueStyle,
  show = true,
  autoFocus = false,
  suppressPasswordManagers = false,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  if (!show) return null;

  return (
    <div
      key={name}
      onClick={() => ref.current?.focus()}
      style={paddingOverride ? { padding: paddingOverride } : {}}
      className={join([styles.Field, ...insertIf(!fullWidth, styles.Short)])}
    >
      {data && (
        <div className={styles.AvatarContainer}>
          <Avatar data={data} />
        </div>
      )}
      <h4 className={boldLabel ? styles.Bold : styles.Regular}>{label}</h4>
      <div className={styles.ContentContainer}>
        {children ? (
          children
        ) : editable ? (
          <input
            ref={ref}
            style={{
              textAlign: isEditing ? 'left' : 'right',
            }}
            autoFocus={autoFocus}
            type={type ?? 'text'}
            name={name ?? label}
            alt={alt ?? name}
            value={value}
            placeholder={placeholder}
            onFocus={(event) => {
              setIsEditing(true);
              onFocus && onFocus(event);
            }}
            onChange={onChange}
            onBlur={(event) => {
              setIsEditing(false);
              onFocusOut && onFocusOut(event);
            }}
            required={required}
            minLength={minLength}
            maxLength={maxLength}
            data-lpignore={suppressPasswordManagers ? 'true' : 'false'}
          />
        ) : (
          <p style={nonEditableValueStyle}>{value}</p>
        )}
      </div>
    </div>
  );
};

export default Field;
